import type { OTCDealData } from '@src/types/OTC';
import { DEAL, logger, useObservableValue, useSubscription, type WebsocketRequest } from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { map } from 'rxjs/operators';

interface DealRequest extends WebsocketRequest {
  name: typeof DEAL;
  DealIDs: string[];
}

type DealSnapshot = {
  deal: OTCDealData | undefined;
};

export function useDeal({ dealID, panelID }: { dealID: string; panelID: string }): {
  deal: OTCDealData | undefined;
  isLoading: boolean;
} {
  const request = useMemo(
    () =>
      ({
        name: DEAL,
        // Backend filters the Deal stream by DealIDs; currently a single-element array per panel subscription.
        DealIDs: [dealID],
        tag: `DealCashflowsHeader/${panelID}/${dealID}`,
      }) satisfies DealRequest,
    [dealID, panelID]
  );
  const { data: dealData, isLoading: isSubscriptionLoading, error } = useSubscription<OTCDealData>(request);

  const subscriptionError = useObservableValue(() => error, [error]);
  const hasSubscriptionError = subscriptionError != null;

  useEffect(() => {
    if (subscriptionError == null) {
      return;
    }
    logger.error(new Error('Deal cashflows header Deal subscription failed'), {
      extra: {
        dealID,
        panelID,
        tag: request.tag,
        code: subscriptionError.error?.code,
        msg: subscriptionError.error?.msg,
        reqid: subscriptionError.reqid,
      },
    });
  }, [subscriptionError, dealID, panelID, request.tag]);

  const snapshot = useObservableValue(
    () =>
      dealData.pipe(
        map(
          (response): DealSnapshot => ({
            deal: response.data[0],
          })
        )
      ),
    [dealData]
  );

  const deal = !hasSubscriptionError && snapshot != null ? snapshot.deal : undefined;
  const isLoading = !hasSubscriptionError && (isSubscriptionLoading || snapshot == null);

  return {
    deal,
    isLoading,
  };
}
